import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { tradingServices, TradingTransfer, TradingWallet } from '../../services/trading-service'
import { Skeleton } from '../../lib/components/Skeleton'
import { isFeatureEnabled } from '../../lib/utils/feature-flags'
import '../dashboard-v2/dashboard-v2.scss'
import '../wallet/wallet.scss'
import './wallet-public.scss'

const usd = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === ''
    ? '—'
    : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

// Statuses after which a transfer no longer changes: instant funding
// (EXECUTED = buying power already in the account), cash journals to and
// from the investment wallet, and ACH bank transfers from before.
const CLOSED_STATUSES = [
  'EXECUTED',
  'ACTIVITY_CREATED',
  'CORRECT',
  'COMPLETED',
  'COMPLETE',
  'CANCELED',
  'FAILED',
  'REJECTED',
  'REFUSED',
  'DELETED',
  'RETURNED',
]
const isPending = (t: TradingTransfer) => !CLOSED_STATUSES.includes(t.status.toUpperCase())

const STATUS_LABEL: Record<string, string> = {
  INITIATED: 'Processing',
  PENDING: 'Processing',
  QUEUED: 'Processing',
  SENT_TO_CLEARING: 'Processing',
  APPROVED: 'Processing',
  EXECUTED: 'Completed',
  ACTIVITY_CREATED: 'Completed',
  CORRECT: 'Completed',
  COMPLETED: 'Completed',
  COMPLETE: 'Completed',
  CANCELED: 'Cancelled',
  DELETED: 'Cancelled',
  FAILED: 'Failed',
  REJECTED: 'Rejected',
  REFUSED: 'Rejected',
  RETURNED: 'Returned',
}
const transferLabel = (t: TradingTransfer) => {
  if (t.source === 'GREENBACK_WALLET') {
    return t.direction === 'INCOMING' ? 'From Main Wallet' : 'To Main Wallet'
  }
  return t.direction === 'INCOMING' ? 'Bank deposit' : 'Bank withdrawal'
}
/** Held transfers read "Under review" whatever the underlying status. */
const transferStatusLabel = (t: TradingTransfer) =>
  t.held ? 'Under review' : statusLabel(t.status)

const statusLabel = (status: string) => STATUS_LABEL[status.toUpperCase()] || status

const errorMessage = (res: any, fallback: string): string =>
  res?.data?.error?.message || res?.data?.message || fallback

const LEDGER_PREVIEW = 8
const SKELETON_ROWS = 4
const LEDGER_EXPANDED = 50

/** One pending transfer; held ones explain the review and can be cancelled. */
const PendingTransferRow = ({ t, onChanged }: { t: TradingTransfer; onChanged: () => void }) => {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const cancel = async () => {
    setBusy(true)
    setError('')
    const res = await tradingServices.cancelTransfer(t.id)
    setBusy(false)
    setConfirming(false)
    if (res?.status === 200) onChanged()
    else {
      setError(errorMessage(res, 'Could not cancel the transfer.'))
      // It may have completed meanwhile; show the latest state.
      onChanged()
    }
  }

  const refundNote =
    t.direction === 'INCOMING'
      ? `The ${usd(t.amount)} goes back to your investment wallet.`
      : 'The cash stays in your brokerage account.'

  return (
    <li className='wallet-page__pending-row'>
      <div>
        <p className='wallet-page__pending-title'>
          {transferLabel(t)} ·{' '}
          <span
            className={
              t.direction === 'INCOMING'
                ? 'wallet-page__amount--credit'
                : 'wallet-page__amount--debit'
            }
          >
            {usd(t.amount)}
          </span>
        </p>
        <p className='wallet-page__pending-meta'>Requested {formatDateTime(t.createdAt)}</p>
        {t.held ? (
          <p className='wallet-public__hint'>
            Larger transfers are reviewed by our brokerage partner before they complete. This can
            take longer than usual.
          </p>
        ) : null}
        {confirming ? (
          <div className='wallet-public__confirm' role='group' aria-label='Confirm cancel'>
            <span>Cancel this transfer? {refundNote}</span>
            <button
              type='button'
              className='wallet-public__link-btn'
              disabled={busy}
              onClick={() => void cancel()}
            >
              {busy ? 'Cancelling…' : 'Yes, cancel'}
            </button>
            <button
              type='button'
              className='wallet-public__link-btn'
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              Keep it
            </button>
          </div>
        ) : t.cancellable ? (
          <button
            type='button'
            className='wallet-public__link-btn'
            onClick={() => setConfirming(true)}
          >
            Cancel transfer
          </button>
        ) : null}
        {error ? <p className='wallet-public__error'>{error}</p> : null}
      </div>
      <span className='wallet-page__pending-status'>{transferStatusLabel(t)}</span>
    </li>
  )
}

type TransferPanelProps = {
  title: string
  availableLabel: string
  available: string | null
  hint: string
  /** Extra note shown under the hint, e.g. that large transfers are reviewed. */
  notice?: string | null
  submitText: string
  busyText: string
  errorFallback: string
  submit: (amount: number) => Promise<any>
  onDone: () => void
}

/** Amount form for moving money between the investment wallet and this account. */
const TransferPanel = ({
  title,
  availableLabel,
  available,
  hint,
  notice,
  submitText,
  busyText,
  errorFallback,
  submit,
  onDone,
}: TransferPanelProps) => {
  const [amount, setAmount] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const availableNum = Number(available) || 0
  const amountNum = Number(amount)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    const res = await submit(amountNum)
    setBusy(false)
    if (res?.status === 201) onDone()
    else setError(errorMessage(res, errorFallback))
  }

  return (
    <section className='wallet-public__panel'>
      <form onSubmit={onSubmit} className='wallet-public__form'>
        <h2>{title}</h2>
        <p className='wallet-public__hint'>
          {availableLabel}: {usd(available)}
        </p>
        <label className='wallet-public__field'>
          <span>Amount (USD)</span>
          <input
            type='number'
            min='0.01'
            step='0.01'
            max={availableNum || undefined}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </label>
        <p className='wallet-public__hint'>{hint}</p>
        {notice ? <p className='wallet-public__notice'>{notice}</p> : null}
        {error ? <p className='wallet-public__error'>{error}</p> : null}
        <button
          type='submit'
          className='wallet-page__funds-btn'
          disabled={busy || !(amountNum > 0) || amountNum > availableNum}
        >
          {busy ? busyText : submitText}
        </button>
      </form>
    </section>
  )
}

const WalletPublicContainer = () => {
  const navigate = useNavigate()
  const [wallet, setWallet] = useState<TradingWallet | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [noAccount, setNoAccount] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [panel, setPanel] = useState<'add' | 'withdraw' | null>(null)
  const [ledgerExpanded, setLedgerExpanded] = useState(false)

  const load = useCallback(async () => {
    setRefreshing(true)
    const res = await tradingServices.getWallet()
    if (res?.status === 200) {
      setWallet(res.data)
      setNoAccount(false)
      setLoadError('')
    } else if (res?.status === 404) {
      setNoAccount(true)
    } else {
      setLoadError(errorMessage(res, 'The public markets wallet is unavailable right now.'))
    }
    setRefreshing(false)
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const transfers = useMemo(() => wallet?.transfers ?? [], [wallet])
  const pending = useMemo(() => transfers.filter(isPending), [transfers])
  const visible = transfers.slice(0, ledgerExpanded ? LEDGER_EXPANDED : LEDGER_PREVIEW)

  const reviewNotice = wallet?.transferReviewThresholdUsd
    ? `Transfers over ${usd(
        wallet.transferReviewThresholdUsd,
      )} may be reviewed before they complete, which can take longer.`
    : null

  /** The value once the wallet has loaded, a placeholder until then. */
  const value = (render: (w: TradingWallet) => React.ReactNode, width: string, height = '1em') =>
    wallet ? render(wallet) : <Skeleton width={width} height={height} />

  return (
    <div className='dashboard-v2 wallet-page' data-testid='wallet-public-page' aria-busy={loading}>
      <div className='dashboard-v2__back-wrap'>
        <button type='button' className='secondary small' onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>

      <header className='wallet-page__header'>
        <div>
          <p className='wallet-page__eyebrow'>Wallet</p>
          <h1>Public markets</h1>
          <p className='wallet-page__subtitle'>
            Cash and activity in your brokerage account for US-listed stocks and ETFs.
          </p>
        </div>
      </header>

      {loadError ? <p className='wallet-public__error'>{loadError}</p> : null}

      {noAccount ? (
        <section className='wallet-page__funds-card'>
          <p className='wallet-page__funds-label'>No brokerage account yet</p>
          <p className='wallet-page__funds-hint'>Open one to trade stocks and add funds.</p>
          {isFeatureEnabled('trading') ? (
            <div className='wallet-page__funds-actions'>
              <button
                type='button'
                className='wallet-page__funds-btn'
                onClick={() => navigate('/dashboard/trading')}
              >
                Open account
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {wallet || loading ? (
        <>
          <section className='wallet-page__funds-card'>
            <div className='wallet-page__funds-label-row'>
              <p className='wallet-page__funds-label'>Available cash</p>
              <button
                type='button'
                className='wallet-page__funds-refresh'
                aria-label='Refresh balance'
                disabled={refreshing || loading}
                onClick={() => void load()}
              >
                ↻
              </button>
            </div>
            <h2 className='wallet-page__funds-value'>
              {value((w) => usd(w.cash), '160px', '36px')}
            </h2>
            <p className='wallet-page__funds-hint'>
              {value(
                (w) => (
                  <>
                    Buying power {usd(w.buyingPower)} · Account {w.accountNumber || '—'} ·{' '}
                    {w.status}
                  </>
                ),
                '280px',
              )}
            </p>
            <div className='wallet-page__funds-actions'>
              <button
                type='button'
                className='wallet-page__funds-btn'
                disabled={wallet?.status !== 'ACTIVE'}
                onClick={() => setPanel((p) => (p === 'add' ? null : 'add'))}
              >
                Add funds
              </button>
              <button
                type='button'
                className='wallet-page__funds-btn'
                disabled={wallet?.status !== 'ACTIVE'}
                onClick={() => setPanel((p) => (p === 'withdraw' ? null : 'withdraw'))}
              >
                Withdraw
              </button>
              {isFeatureEnabled('trading') ? (
                <button
                  type='button'
                  className='wallet-page__funds-btn'
                  onClick={() => navigate('/dashboard/trading')}
                >
                  Trade stocks
                </button>
              ) : null}
              <button
                type='button'
                className='wallet-page__funds-btn'
                onClick={() => navigate('/dashboard/wallet')}
              >
                Investment wallet
              </button>
            </div>
          </section>

          <section className='wallet-public__summary'>
            <div>
              <span>Portfolio value</span>
              <strong>{value((w) => usd(w.portfolioValue), '110px')}</strong>
            </div>
            <div>
              <span>Invested in stocks</span>
              <strong>{value((w) => usd(w.investedValue), '110px')}</strong>
            </div>
            <div>
              <span>Equity</span>
              <strong>{value((w) => usd(w.equity), '110px')}</strong>
            </div>
          </section>

          {wallet && panel === 'add' ? (
            <TransferPanel
              title='Add funds from your investment wallet'
              availableLabel='Available in investment wallet'
              available={wallet.greenbackWalletAvailable}
              hint='The amount moves from your investment wallet and is available to trade right away.'
              notice={reviewNotice}
              submitText='Add funds'
              busyText='Adding…'
              errorFallback='Could not add the funds.'
              submit={tradingServices.fundFromWallet}
              onDone={() => {
                setPanel(null)
                void load()
              }}
            />
          ) : null}

          {wallet && panel === 'withdraw' ? (
            <TransferPanel
              title='Withdraw to your investment wallet'
              availableLabel='Available to withdraw'
              available={wallet.withdrawable ?? null}
              hint='Cash moves back to your investment wallet once the transfer completes, usually within minutes. Cash from recent sales may take a day to become available.'
              notice={reviewNotice}
              submitText='Withdraw'
              busyText='Withdrawing…'
              errorFallback='Could not start the withdrawal.'
              submit={tradingServices.withdrawToWallet}
              onDone={() => {
                setPanel(null)
                void load()
              }}
            />
          ) : null}

          {pending.length > 0 ? (
            <section className='wallet-page__pending'>
              <h2>Pending transfer</h2>
              <ul className='wallet-page__pending-list'>
                {pending.map((t) => (
                  <PendingTransferRow key={t.id} t={t} onChanged={() => void load()} />
                ))}
              </ul>
            </section>
          ) : null}

          <section className='wallet-page__ledger'>
            <h2>Transaction ledger</h2>
            {wallet?.bankHistoryUnavailable ? (
              <p className='wallet-public__hint'>
                Earlier bank transfers can&apos;t be loaded right now and are not shown.
              </p>
            ) : null}
            {!wallet ? (
              <div className='wallet-page__ledger-scroll'>
                <table>
                  <thead>
                    <tr>
                      <th>Transaction</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                      <tr key={i} className='wallet-page__ledger-row'>
                        <td>
                          <Skeleton width='150px' />
                        </td>
                        <td>
                          <Skeleton width='70px' />
                        </td>
                        <td>
                          <Skeleton width='90px' />
                        </td>
                        <td>
                          <Skeleton width='80px' />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : transfers.length === 0 ? (
              <p className='wallet-page__empty'>No transfers yet.</p>
            ) : (
              <>
                <div className='wallet-page__ledger-scroll'>
                  <table>
                    <thead>
                      <tr>
                        <th>Transaction</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((t) => (
                        <tr key={t.id} className='wallet-page__ledger-row'>
                          <td>{transferLabel(t)}</td>
                          <td>
                            {t.direction === 'INCOMING' ? '+' : '-'}
                            {usd(t.amount)}
                          </td>
                          <td>{formatDateTime(t.createdAt)}</td>
                          <td>{transferStatusLabel(t)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {transfers.length > LEDGER_PREVIEW ? (
                  <button
                    type='button'
                    className='wallet-page__ledger-toggle'
                    onClick={() => setLedgerExpanded((prev) => !prev)}
                  >
                    {ledgerExpanded ? 'Show less' : 'View more'}
                  </button>
                ) : null}
              </>
            )}
          </section>
        </>
      ) : null}
    </div>
  )
}

export { WalletPublicContainer }
